import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useDebounce } from 'use-debounce/lib';
import times from 'lodash/times';
import moment from 'moment';
import { createPortal } from 'react-dom';
import { updateTransactionFilters } from '../../../../../../state/actions';
import { useTransactions } from '../../../../../../hooks/useTransactions';
import { Box } from '../../../../../../../CommonComponents/OPL/Box/Box';
import { Text } from '../../../../../../../CommonComponents/OPL/Text/Text';
import { Button } from '../../../../../../../CommonComponents/OPL/Button/Button';
import { HtmlTable } from '../../../../../../../CommonComponents/OPL/HtmlTable/HtmlTable';
import { Input } from '../../../../../../../CommonComponents/OPL/Input/Input';
import { RawButton } from '../../../../../../../CommonComponents/OPL/Button/Button.styles';
import { SortBy } from '../../../../Transactions.meta';
import { Icon } from '../../../../../../../CommonComponents/OPL/Icon/Icon';
import { TableLoading } from '../TableLoading/TableLoading';
import { formatCurrency, formatNumber } from '../../../../../../../CommonUtils/formatNumber';
import { Tooltip } from '../../../../../../../CommonComponents/OPL/Tooltip/Tooltip';
import { useFeatureFlag } from '../../../../../../../_hooks/useFeatureFlag';
import { Feature } from '../../../../../../../CommonSelectors/CommonArea.types';
import { TruncatedText } from '../../../../../../../CommonComponents/OPL/Text/TruncatedText';
import { Card } from '../../../../../../../CommonComponents/OPL/Card/Card';
import { ErrorState } from '../../../../../../../CommonComponents/ErrorState/ErrorState';
import { ActivityCsvExport } from '../../../../../../components/ActivityCsvExport/ActivityCsvExport';
import { useExportCsvButtonContainer } from '../../../../../../hooks/useExportCsvButtonContainer';

interface Props {
  managedAccountId?: string;
}

export const TransactionsTable = () => {
  const dispatch = useDispatch();

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm] = useDebounce(searchTerm, 1000);

  const exportCsvButtonContainer = useExportCsvButtonContainer();

  useEffect(() => {
    dispatch(updateTransactionFilters({ query: debouncedSearchTerm }));
  }, [debouncedSearchTerm, dispatch]);

  const { hasNextPage, fetchNextPage } = useTransactions();

  return (
    <>
      <TableContent searchTerm={searchTerm} setSearchTerm={setSearchTerm} />

      {hasNextPage && (
        <Box display="flex" justifyContent="center" width="100%" py="24">
          <Button
            onClick={() => {
              fetchNextPage();
            }}
          >
            Load more transactions
          </Button>
        </Box>
      )}

      {!!exportCsvButtonContainer &&
        createPortal(<ActivityCsvExport tab="activity" />, exportCsvButtonContainer)}
    </>
  );
};

interface Props {
  setSearchTerm(arg: string): void;
  searchTerm: string;
}

export const TableContent = ({ setSearchTerm, searchTerm }: Props) => {
  const dispatch = useDispatch();

  const {
    data: { transactionFilters, ...transactionsData },
    isFetching,
    isError,
    refetch,
    isFetchingNextPage,
  } = useTransactions();

  const showConfirmNotes = useFeatureFlag(Feature.ShowTransactionConfirmNotes);

  return (
    <Card>
      <Box display="flex" flexDirection="column" p="12">
        <Input
          placeholder="Search..."
          leftIcon="fa fa-search"
          onChange={({ target: { value: filter } }) => {
            setSearchTerm(filter);
          }}
          readOnly={isFetching}
          value={searchTerm}
          aria-label="Search"
        />
      </Box>

      <HtmlTable variant="embedded" columnSpacing="small" maxHeight="60vh" fixedHead>
        <table>
          <thead>
            <tr>
              <th>
                <RawButton
                  onClick={() => {
                    dispatch(
                      updateTransactionFilters({
                        sortBy: SortBy.Date,
                        sortDesc: !transactionFilters.sortDesc,
                      }),
                    );
                  }}
                  aria-label="Sort by column"
                  css="width: 100%"
                >
                  <Box display="flex" alignItems="center" css="gap: 4px" p="2">
                    <Text variant="tableHeader">Date</Text>

                    {transactionFilters.sortBy === SortBy.Date && (
                      <Icon
                        className={
                          transactionFilters.sortDesc ? 'fas fa-arrow-down' : 'fas fa-arrow-up'
                        }
                        color="neutral.500"
                      />
                    )}
                  </Box>
                </RawButton>
              </th>
              <th>
                <RawButton
                  onClick={() => {
                    dispatch(
                      updateTransactionFilters({
                        sortBy: SortBy.Name,
                        sortDesc: !transactionFilters.sortDesc,
                      }),
                    );
                  }}
                  aria-label="Sort by column"
                  css="width: 100%"
                >
                  <Box display="flex" alignItems="center" css="gap: 4px" p="2">
                    <Text variant="tableHeader">Name</Text>

                    {transactionFilters.sortBy === SortBy.Name && (
                      <Icon
                        className={
                          transactionFilters.sortDesc ? 'fas fa-arrow-down' : 'fas fa-arrow-up'
                        }
                        color="neutral.500"
                      />
                    )}
                  </Box>
                </RawButton>
              </th>
              <th>
                <RawButton
                  onClick={() => {
                    dispatch(
                      updateTransactionFilters({
                        sortBy: SortBy.Description,
                        sortDesc: !transactionFilters.sortDesc,
                      }),
                    );
                  }}
                  aria-label="Sort by column"
                  css="width: 100%"
                >
                  <Box display="flex" alignItems="center" css="gap: 4px" p="2">
                    <Text variant="tableHeader">Transaction Type</Text>

                    {transactionFilters.sortBy === SortBy.Description && (
                      <Icon
                        className={
                          transactionFilters.sortDesc ? 'fas fa-arrow-down' : 'fas fa-arrow-up'
                        }
                        color="neutral.500"
                      />
                    )}
                  </Box>
                </RawButton>
              </th>

              {showConfirmNotes && (
                <th>
                  <Text variant="tableHeader" textAlign="left">
                    Confirm Notes
                  </Text>
                </th>
              )}

              <th>
                <Text variant="tableHeader" textAlign="right">
                  Account number
                </Text>
              </th>
              <th>
                <Text variant="tableHeader" textAlign="right">
                  Units
                </Text>
              </th>
              <th>
                <Text variant="tableHeader" textAlign="right">
                  Price
                </Text>
              </th>
              <th>
                <Text variant="tableHeader" textAlign="right">
                  Total
                </Text>
              </th>
            </tr>
          </thead>

          <tbody>
            {isError && (
              <tr>
                <td>
                  <ErrorState
                    onRetry={() => {
                      refetch();
                    }}
                  />
                </td>
              </tr>
            )}

            {!isFetching && transactionsData?.data && transactionsData.data[0].length === 0 && (
              <tr>
                <td>
                  <Text>There are no transactions available.</Text>
                </td>
              </tr>
            )}

            {isFetching && !isFetchingNextPage
              ? times(20).map(key => <TableLoading key={key} />)
              : transactionsData?.data &&
                transactionsData.data.map(page =>
                  page.map((transaction, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <tr key={index}>
                      <td>
                        <Text fontWeight={500}>{moment(transaction.date).format('M/D/YYYY')}</Text>
                      </td>

                      <td>
                        <Box maxWidth={280}>
                          <TruncatedText fontWeight={500}>{transaction.name}</TruncatedText>
                        </Box>
                      </td>

                      <td style={{ whiteSpace: 'nowrap' }}>
                        <Text fontWeight={500} style={{ display: 'inline-block' }}>
                          {transaction.description}
                        </Text>
                        {transaction.notes && (
                          <Tooltip content={transaction.notes} placement="top">
                            <Icon
                              className="far fa-info-circle"
                              color="neutral.500"
                              fontSize="small"
                              ml="4"
                            />
                          </Tooltip>
                        )}
                      </td>

                      {showConfirmNotes && (
                        <td>
                          <Box maxWidth={160}>
                            <TruncatedText fontWeight={500}>
                              {transaction.confirmNotes || ''}
                            </TruncatedText>
                          </Box>
                        </td>
                      )}

                      <td>
                        <Text fontWeight={500} variant="tabular" textAlign="right">
                          {transaction.accountNumber}
                        </Text>
                      </td>
                      <td>
                        <Text fontWeight={500} variant="tabular" textAlign="right">
                          {transaction.units &&
                            formatNumber(transaction.units, { decimalPlaces: 2 })}
                        </Text>
                      </td>
                      <td>
                        <Text fontWeight={500} variant="tabular" textAlign="right">
                          {transaction.price &&
                            formatCurrency(transaction.price, { decimalPlaces: 2 })}
                        </Text>
                      </td>
                      <td>
                        <Text fontWeight={500} variant="tabular" textAlign="right">
                          {transaction.total &&
                            formatCurrency(transaction.total, { decimalPlaces: 2 })}
                        </Text>
                      </td>
                    </tr>
                  )),
                )}
            {isFetchingNextPage && times(20).map(key => <TableLoading key={key} />)}
          </tbody>
        </table>
      </HtmlTable>
    </Card>
  );
};
